import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@components/Drawer';
import ExpandedSection from '@components/ExpandedSection';
import { accountActions, alertActions, googleActions } from '@actions';
import { useTranslation } from '@hooks';
import { handleApiError } from '@helpers/api';
import { companyService } from '@services';
import GoogleCompanyCard from './GoogleCompanyCard';

export const Progress = styled(CircularProgress)({
    color: 'rgba(0, 0, 0, 0.38)',
    width: '58px !important',
    height: '58px !important',
    display: 'block',
    margin: '0 auto 24px',
});

const ExpandedSectionWrapper = styled(ExpandedSection)({
    marginBottom: '16px',
});

const ImportGoogleCompanies = ({ isOpen, onClose }) => {
    const [companies, setCompanies] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [section, setSection] = useState({});

    const account = useSelector(state => state.account?.account);
    const LOCATIONS = useSelector(state => state.google.data);
    const locationsLoading = useSelector(state => state.google.loading);

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const MAX_AVAILABLE_COMPANIES_COUNT = account?.companiesMaxCount - account?.companies?.length;

    useEffect(() => {
        if (!LOCATIONS.length) {
            dispatch(googleActions.get());
        } else {
            setSection(
                Object.fromEntries(
                    [...new Set(LOCATIONS.map(l => l.accountName))].map(accountName => [accountName, true]),
                ),
            );
        }
    }, [dispatch, LOCATIONS.length]);

    const onCheckCompany = location => {
        if (companies.some(c => c.locationId === location.locationId)) {
            setCompanies(companies.filter(c => c.locationId !== location.locationId));
            return;
        }
        if (companies.length < MAX_AVAILABLE_COMPANIES_COUNT) {
            setCompanies([...new Set([...companies, location])]);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await companyService.importGoogleCompanies(companies);
            await dispatch(accountActions.get());
            dispatch(alertActions.success(t('alertMessages.importSuccess')));
        } catch (error) {
            handleApiError(error, 'alertMessages.importFail');
            if (error.errorCode === 'profile_connected_to_another_location') {
                await dispatch(accountActions.get());
            }
        } finally {
            onClose();
            dispatch(googleActions.reset());
            setIsSubmitting(false);
        }
    };

    const companiesByAccount = useMemo(() => {
        return Object.groupBy(LOCATIONS, l => l.accountName);
    }, [LOCATIONS]);

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            title={t('SettingsCompanies.importCompanies')}
            subtitle={t('SettingsCompanies.selectCompanies')}
            primaryText={t('SettingsCompanies.importCompanies')}
            primaryDisabled={!companies.length || isSubmitting}
            primaryAction={handleSubmit}
            secondaryText={t('buttons.cancel')}
        >
            {!locationsLoading ? (
                Object.entries(companiesByAccount).map(([accountName, locations]) => (
                    <ExpandedSectionWrapper
                        key={accountName}
                        isOpen={section[accountName]}
                        onClick={() => setSection(prev => ({ ...prev, [accountName]: !prev[accountName] }))}
                        text={accountName}
                    >
                        <div style={{ display: 'grid', gridGap: '8px' }}>
                            {locations.map(l => (
                                <GoogleCompanyCard
                                    key={l.name}
                                    location={l}
                                    selectedCompanies={companies}
                                    setCompany={onCheckCompany}
                                />
                            ))}
                        </div>
                    </ExpandedSectionWrapper>
                ))
            ) : (
                <Progress />
            )}
        </Drawer>
    );
};

export default ImportGoogleCompanies;
